import { getFunctions, httpsCallable } from 'firebase/functions';
import Col from 'react-bootstrap/Col';

import { uniqBy } from 'lodash';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../util/auth';
import { getConfiguration } from '../../util/config';
import { RULES, SCREEN } from '../../util/Constant';
import {
  checkDefaultSetupDataForScheduledData,
  createConfiguration,
  createScheduledSamples,
  createSilencedData,
  deleteScheduledSample,
  getControlStrategyConfigurationByFullKey,
  updateConfiguration,
  getIndexValues,
  useGetInstruments,
  useGetMethods,
  useGetProducts,
  useGetSamples,
  useGetScheduledSamples,
  useGetSilencedDataByShortKey,
  useGetTypes
} from '../../util/db';
import firebaseApp, { getCurrentFeatureFlags } from '../../util/firebase';
import {
  checkReadOnly,
  renderAuthButtonWithPermission,
  renderButtonWithPermissions,
  textSort
} from '../../util/util';
import { dayTimeComparator } from '../Columns';
import CustomTable from '../Table/CustomTable';
import ChartContext from './DataEntryProvider';
import './DataEntrySection.scss';
import ModalError from '../Common/modal/ModalError';

const functions = getFunctions(firebaseApp);
// spc* variants: the unprefixed functions are owned by InsightSQC in shared
// projects and reject variation="spc" post-split. This component is only
// mounted from the SPC page in this app.
const addMultipleDataFunction = httpsCallable(functions, 'spcAddMultipleData', {
  timeout: 540000
});
const runMultipleDataFunction = httpsCallable(functions, 'spcRunMultipleData', {
  timeout: 540000
});
const runDataFunction = httpsCallable(functions, 'runData', { timeout: 540000 });
const spcRunDataFunction = httpsCallable(functions, 'spcRunData');
const config = getConfiguration(window.location.hostname);

function DataEntrySection({
  locations,
  variation = '',
  tz = 'UTC',
  exportFullKeysToCSV,
  currentLocation,
  setIndexes
}) {
  const auth = useAuth();
  const history = useHistory();
  const currentTime = `${moment(Date.now()).format('MM/DD/YYYY')} 12:00 PM`;
  const [currentInstrumentsDropdown, setCurrentInstrumentsDropdown] = useState([]);
  const [currentLocations, setCurrentLocations] = useState([]);
  const { data: methods } = useGetMethods(variation);
  const { data: instruments } = useGetInstruments(variation);
  const { data: types } = useGetTypes(variation);
  const { data: products } = useGetProducts(variation);
  const [currentTypesDropdown, setCurrentTypesDropdown] = useState([]);
  const [currentProductsDropdown, setCurrentProductsDropdown] = useState([]);
  const [currentMethodsDropdown, setCurrentMethodsDropdown] = useState([]);
  const [currentSamplesDropdown, setCurrentSamplesDropdown] = useState([]);
  const { data: samples } = useGetSamples(variation);
  const [currentPersonnelDropdown, setCurrentPersonnelDropdown] = useState([]);
  const [radioValue, setRadioValue] = useState('1');
  // eslint-disable-next-line no-unused-vars
  const [show, setShow] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [showDataAlert, setShowDataAlert] = useState(false);
  const [recheckAlert, setRecheckAlert] = useState(false);
  const [addDataAlert, setAddDataAlert] = useState(false);
  const [configAlert, setConfigAlert] = useState(false);

  const [currentSilencedData, setCurrentSilencedData] = useState([]);
  const [dataToRecheck, setDataToRecheck] = useState([]);
  const {
    currentChartGroupConfigurations,
    currentShortKey,
    chartData,
    permissionData,
    personnel,
    currentPersonnel,
    summaries
  } = useContext(ChartContext);
  const { data: scheduledSamples } = useGetScheduledSamples(locations, variation);
  const { data: silencedData } = useGetSilencedDataByShortKey(currentShortKey, variation);
  const [currentScheduledSamples, setCurrentScheduledSamples] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const featureFlags = getCurrentFeatureFlags();

  const [radios] = useState([
    { name: 'Scheduled & Recheck Data', value: '1' },
    { name: 'Silenced Data', value: '2' }
  ]);
  const [deleteList, setDeleteList] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [configLoad, setConfigLoad] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const silenceOptions = [
    {
      value: 'No',
      label: 'No'
    },
    {
      value: 'Yes',
      label: 'Yes'
    }
  ];
  const pageSizeDefault = 20;
  const isSpcPage = history.location.pathname.includes('/spc');
  useEffect(() => {
    if (
      Array.isArray(scheduledSamples) &&
      scheduledSamples.length > 0 &&
      currentPersonnel.length > 0
    ) {
      const fetchSetupData = async () => {
        const matchingUser = currentPersonnel.find((person) => person.email === auth.user.email);
        const additionalProperties = {
          personnel: matchingUser
            ? config.useAbbreviationPersonnelName
              ? matchingUser?.abbreviation || matchingUser?.name || ''
              : matchingUser?.name || ''
            : auth.user.email,
          result: ''
        };

        const mappedSamples = await Promise.all(
          scheduledSamples.map(async (sample) => {
            let ucl_lcl = {};
            const findSummary = summaries.find((item) => item.id === sample.fullKey);
            if (findSummary) {
              ucl_lcl = {
                lowerControlLimit: Math.round(findSummary.lcl_sd * 100) / 100,
                upperControlLimit: Math.round(findSummary.ucl_sd * 100) / 100
              };
            }

            const onSetupData = await checkDefaultSetupDataForScheduledData(sample.fullKey);

            return {
              ...sample,
              ...additionalProperties,
              ...ucl_lcl,
              setupData: onSetupData ? 1 : 0
            };
          })
        );

        setCurrentScheduledSamples(mappedSamples);
      };
      fetchSetupData();
    } else {
      setCurrentScheduledSamples([]);
    }
  }, [scheduledSamples]);

  const handleRadioChange = (e) => {
    if (e.currentTarget.value === '2') {
      setRecheckAlert(false);
    } else if (dataToRecheck.length > 0) {
      setRecheckAlert(true);
    }

    setRadioValue(e.currentTarget.value);
  };

  useEffect(() => {
    if (Array.isArray(silencedData) && silencedData.length > 0) {
      setCurrentSilencedData(silencedData);
    }
  }, [silencedData]);

  useEffect(() => {
    if (Array.isArray(samples) && samples.length > 0) {
      samples.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      const samplesDropdown = samples.map((sample) => {
        const sampleToReturn = {
          id: sample.id,
          value: sample.name,
          label: sample.name
        };

        return sampleToReturn;
      });
      setCurrentSamplesDropdown(samplesDropdown);
    }
  }, [samples]);

  useEffect(() => {
    if (Array.isArray(locations) && locations.length > 0) {
      const locationsAssigned = [];
      locations.forEach((location) => {
        const locationToReturn = {
          value: location.name,
          label: location.name
        };
        locationsAssigned.push(locationToReturn);
      });

      setCurrentLocations(locationsAssigned);
    }
  }, [locations]);

  useEffect(() => {
    const updatedDataToRecheck = [];
    const oocDataToRecheck = structuredClone(chartData).filter((dataPoint) =>
      dataPoint.flags.includes('OOC')
    );
    oocDataToRecheck.forEach((dataPoint) => {
      if (typeof dataPoint.sampleId === 'string' && !dataPoint.sampleId.includes('_recheck')) {
        const newDataPoint = { ...dataPoint };
        newDataPoint.sampleId += '_recheck';
        // add data point with same data but updated sample ID
        newDataPoint.flags = '';
        newDataPoint.silenceReason = '';
        newDataPoint.result = '';
        updatedDataToRecheck.push(newDataPoint);
      }
    });
    setDataToRecheck(updatedDataToRecheck);
  }, [chartData]);

  useEffect(() => {
    if (currentChartGroupConfigurations.autoCreateSampleRecheck === 'Yes') {
      setRecheckAlert(true);
    } else {
      setRecheckAlert(false);
    }
  }, [currentChartGroupConfigurations.autoCreateSampleRecheck]);

  const getDropdown = (array) => {
    if (Array.isArray(array) && array.length > 0) {
      array.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return String(nameA).localeCompare(String(nameB));
      });
    }
    const uniques = uniqBy(array, (item) => item.name) || [];
    const dropdown = uniques.map((item) => {
      const itemToReturn = {
        id: item.id,
        value: item.name,
        label: item.name
      };
      return itemToReturn;
    });
    return dropdown;
  };

  useEffect(() => {
    setCurrentTypesDropdown(getDropdown(types));
  }, [types]);

  useEffect(() => {
    setCurrentProductsDropdown(getDropdown(products));
  }, [products]);

  useEffect(() => {
    if (Array.isArray(personnel) && personnel.length > 0) {
      const personnelValue = personnel.map((item) => {
        return {
          ...item,
          name: config.useAbbreviationPersonnelName
            ? item?.abbreviation || item?.name || ''
            : item?.name || ''
        };
      });
      setCurrentPersonnelDropdown(getDropdown(personnelValue));
    }
  }, [personnel]);

  useEffect(() => {
    setCurrentInstrumentsDropdown(getDropdown(instruments));
  }, [instruments]);

  useEffect(() => {
    (async () => {
      const result = await getIndexValues();
      if (setIndexes) {
        setIndexes(result?.data);
      }
    })();
  }, [addDataAlert]);

  const renderAlert = (renderRecheckAlert) => {
    if (renderRecheckAlert) {
      return (
        <Alert key="warning" onClick={() => setShow(false)} dismissible variant="warning">
          You have {dataToRecheck.length} samples that have been flagged for recheck.
        </Alert>
      );
    }
    return <></>;
  };

  const renderDataSuccessAlert = (dataAlert) => {
    if (dataAlert) {
      return (
        <Alert key="success" onClick={() => setAddDataAlert(false)} dismissible variant="success">
          Successfully added data
        </Alert>
      );
    }
    return <></>;
  };
  const addedConfigAlert = (configAlert) => {
    if (configAlert) {
      return (
        <Alert key="success" onClick={() => setConfigAlert(false)} dismissible variant="success">
          Successfully added configurations
        </Alert>
      );
    }
    return <></>;
  };

  const renderDropdownParameter = (data, key) => {
    const methodSelect = data.row.method;
    const parameterFilter = methods
      .filter((item) => item.method === methodSelect)
      .map((value) => {
        return {
          value: value[key],
          label: value[key]
        };
      });

    const parameterFilterUniq = uniqBy(parameterFilter, (item) => item.label);
    parameterFilterUniq.sort(textSort);
    return parameterFilterUniq;
  };

  const deleteRow = (rowIn) => {
    const row = rowIn;
    setDeleteList([...deleteList, rowIn]);
    setCurrentScheduledSamples(currentScheduledSamples.filter((item) => item.id !== row.id));
  };

  const [dateTimeChanged, setDateTimeChanged] = useState([]);

  const dataEntryTableColumns = [
    {
      field: 'location',
      headerName: 'Location',
      editable: true,
      type: 'singleSelect',
      valueOptions: currentLocations,
      flex: 4
    },
    {
      field: variation === 'spc' ? 'type' : 'method',
      headerName: variation === 'spc' ? 'Type' : 'Method',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentTypesDropdown : currentMethodsDropdown,
      flex: 2
    },
    {
      field: 'parameter',
      headerName: 'Parameter',
      editable: true,
      type: 'singleSelect',
      valueOptions: (data) => {
        return renderDropdownParameter(data, data.field);
      },
      flex: 2
    },
    {
      field: variation === 'spc' ? 'method' : 'instrument',
      headerName: variation === 'spc' ? 'Method' : 'Instrument',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentMethodsDropdown : currentInstrumentsDropdown,
      flex: 2
    },
    {
      field: variation === 'spc' ? 'product' : 'sampleName',
      headerName: variation === 'spc' ? 'Product' : 'Sample Name',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentProductsDropdown : currentSamplesDropdown,
      flex: 2
    },
    {
      field: 'dateTime',
      headerName: 'Date/Time',
      sortComparator: dayTimeComparator,
      editable: true,
      flex: 3,
      valueGetter: (params) => {
        return moment(params.value).format('MM/DD/YYYY HH:mm');
      }
    },
    {
      field: 'personnel',
      headerName: 'Personnel',
      editable: true,
      type: 'singleSelect',
      flex: 3,
      valueOptions: currentPersonnelDropdown
    },
    {
      field: 'sampleId',
      editable: true,
      headerName: 'Sample ID',
      type: 'string',
      flex: 3
    },
    {
      field: 'setupData',
      headerName: 'Setup Data',
      renderCell: (params) => {
        if (params.value == 0 || params.value == 1) return params.value;
        return '';
      },
      editable: true,
      flex: 2
    },
    {
      field: 'result',
      headerName: 'Result',
      editable: true,
      flex: 2
    }
  ];

  const huntTableColumns = [
    {
      field: 'location',
      headerName: 'Location',
      editable: true,
      type: 'singleSelect',
      valueOptions: currentLocations,
      flex: 4
    },
    {
      field: variation === 'spc' ? 'type' : 'method',
      headerName: variation === 'spc' ? 'Type' : 'Method',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentTypesDropdown : currentMethodsDropdown,
      flex: 2
    },
    {
      field: 'parameter',
      headerName: 'Parameter',
      valueOptions: (data) => {
        return renderDropdownParameter(data, data.field);
      },
      editable: true,
      type: 'singleSelect',
      flex: 2
    },
    {
      field: variation === 'spc' ? 'method' : 'instrument',
      headerName: variation === 'spc' ? 'Method' : 'Instrument',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentMethodsDropdown : currentInstrumentsDropdown,
      flex: 2
    },
    {
      field: variation === 'spc' ? 'product' : 'sampleName',
      headerName: variation === 'spc' ? 'Product' : 'Sample Name',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentProductsDropdown : currentSamplesDropdown,
      flex: 2
    },
    {
      field: 'dateTime',
      headerName: 'Date/Time',
      sortComparator: dayTimeComparator,
      editable: true,
      flex: 3,
      valueGetter: (params) => {
        return moment(params.value).format('MM/DD/YYYY HH:mm');
      }
    },
    {
      field: 'personnel',
      headerName: 'Personnel',
      valueOptions: currentPersonnelDropdown,
      editable: true,
      type: 'singleSelect',
      flex: 3
    },
    {
      field: 'sampleId',
      headerName: 'Sample ID',
      type: 'string',
      editable: true,
      flex: 3
    },
    {
      field: 'setupData',
      headerName: 'Setup Data',
      renderCell: (params) => {
        if (params.value == 0 || params.value == 1) return params.value;
        return '';
      },
      editable: true,
      flex: 2
    },
    {
      field: 'lowerControlLimit',
      headerName: 'LCL',
      editable: true,
      flex: 2
    },
    {
      field: 'upperControlLimit',
      headerName: 'UCL',
      editable: true,
      flex: 2
    },
    {
      field: 'result',
      headerName: 'Result',
      editable: true,
      flex: 2
    }
  ];

  const cellEditProp = {
    mode: 'click',
    blurToSave: true,
    afterSaveCell: (oldValue, newValue, rowIn, column) => {
      const row = rowIn;
      row[column.dataField] = newValue;
    }
  };

  const renderScheduledDataTable = (instanceFeatureFlags) => {
    const tableToReturn = [];
    if (instanceFeatureFlags.includes('lcl-data-entry-columns')) {
      tableToReturn.push(
        <>
          <CustomTable
            numberOfRows={pageSizeDefault}
            data={currentScheduledSamples}
            setData={setCurrentScheduledSamples}
            action={['delete', 'edit']}
            header={huntTableColumns}
            deleteRowData={deleteRow}
          />
        </>
      );
    } else {
      tableToReturn.push(
        <>
          <CustomTable
            numberOfRows={pageSizeDefault}
            data={currentScheduledSamples}
            setData={setCurrentScheduledSamples}
            header={dataEntryTableColumns}
            action={['delete', 'edit']}
            deleteRowData={deleteRow}
            onCellClickWhenEditing={(params, event) => {
              if (params.field === 'dateTime') {
                setDateTimeChanged([...dateTimeChanged, params.row.id]);
              }
            }}
          />
        </>
      );
    }
    return tableToReturn;
  };

  const dataEntryTableColumnsReadOnly = [
    {
      field: 'location',
      headerName: 'Location',
      flex: 2,
      minWidth: 300
    },
    {
      field: 'method',
      headerName: 'Method',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'parameter',
      headerName: 'Parameter',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'instrument',
      headerName: 'Instrument',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'sampleName',
      headerName: 'Sample Name',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'dateTime',
      headerName: 'Date/Time',
      sortComparator: dayTimeComparator,
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        return moment(params.value).format('MM/DD/YYYY HH:mm');
      }
    },
    {
      field: 'personnel',
      headerName: 'Personnel',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'sampleId',
      headerName: 'Sample ID',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'setupData',
      headerName: 'Setup Data',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'result',
      headerName: 'Result',
      flex: 1,
      minWidth: 150
    }
  ];

  const addRow = () => {
    const matchingUser = currentPersonnel.find((person) => person.email === auth.user.email);
    const rowToAdd = {
      id: uuidv4(),
      location: '',
      method: '',
      type: '',
      product: '',
      parameter: '',
      instrument: '',
      sampleName: '',
      dateTime: currentTime,
      personnel: matchingUser
        ? config.useAbbreviationPersonnelName
          ? matchingUser?.abbreviation || matchingUser?.name || ''
          : matchingUser?.name || ''
        : auth.user.email,
      sampleId: '',
      result: '',
      setupData: 0,
      delete: '',
      isNew: true
    };
    setCurrentScheduledSamples([...currentScheduledSamples, rowToAdd]);
  };

  const recheckDataEntryTableColumns = [
    {
      field: 'location',
      headerName: 'Location',
      flex: 4,
      valueOptions: currentLocations,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: variation === 'spc' ? 'type' : 'method',
      headerName: variation === 'spc' ? 'Type' : 'Method',
      flex: 2,
      valueOptions: variation === 'spc' ? currentTypesDropdown : currentMethodsDropdown,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: 'parameter',
      headerName: 'Parameter',
      flex: 2,
      valueOptions: (data) => {
        return renderDropdownParameter(data, data.field);
      },
      editable: true,
      type: 'singleSelect'
    },
    {
      field: variation === 'spc' ? 'method' : 'instrument',
      headerName: variation === 'spc' ? 'Method' : 'Instrument',
      flex: 2,
      valueOptions: variation === 'spc' ? currentMethodsDropdown : currentInstrumentsDropdown,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: variation === 'spc' ? 'product' : 'sampleName',
      headerName: variation === 'spc' ? 'Product' : 'Sample Name',
      flex: 2,
      valueOptions: variation === 'spc' ? currentProductsDropdown : currentSamplesDropdown,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: 'dateTime',
      editable: true,
      headerName: 'Date/Time',
      sortComparator: dayTimeComparator,
      flex: 3,
      valueGetter: (params) => {
        return moment(params.value).format('MM/DD/YYYY HH:mm');
      }
    },
    {
      field: 'personnel',
      headerName: 'Personnel',
      flex: 3,
      valueOptions: currentPersonnelDropdown,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: 'sampleId',
      headerName: 'Sample ID',
      editable: true,
      flex: 3
    },
    {
      field: 'setupData',
      headerName: 'Setup Data',
      renderCell: (params) => {
        if (params.value == 0 || params.value == 1) return params.value;
        return '';
      },
      editable: true,
      flex: 2
    },
    {
      field: 'silence',
      headerName: 'Silence?',
      flex: 2,
      valueOptions: silenceOptions,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: 'silenceReason',
      headerName: 'Reason for Silencing',
      editable: true,
      flex: 2
    },
    {
      field: 'result',
      headerName: 'Result',
      editable: true,
      flex: 2
    }
  ];

  const recheckDataEntryTableColumnsReadOnly = [
    {
      field: 'location',
      flex: 4,
      headerName: 'Location'
    },
    {
      field: variation === 'spc' ? 'type' : 'method',
      flex: 2,
      headerName: variation === 'spc' ? 'Type' : 'Method'
    },
    {
      field: 'parameter',
      flex: 2,
      headerName: 'Parameter'
    },
    {
      field: variation === 'spc' ? 'method' : 'instrument',
      flex: 2,
      headerName: variation === 'spc' ? 'Method' : 'Instrument'
    },
    {
      field: variation === 'spc' ? 'product' : 'sampleName',
      flex: 2,
      headerName: variation === 'spc' ? 'Product' : 'Sample Name'
    },
    {
      field: 'dateTime',
      flex: 3,
      headerName: 'Date/Time',
      sortComparator: dayTimeComparator,
      valueGetter: (params) => {
        return moment(params.value).format('MM/DD/YYYY HH:mm');
      }
    },
    {
      field: 'personnel',
      flex: 2,
      headerName: 'Personnel'
    },
    {
      field: 'sampleId',
      flex: 3,
      headerName: 'Sample ID'
    },
    {
      field: 'setupData',
      flex: 2,
      headerName: 'Setup Data'
    },
    {
      field: 'silence',
      flex: 2,
      headerName: 'Silence?'
    },
    {
      field: 'silenceReason',
      flex: 2,
      headerName: 'Reason for Silencing'
    },
    {
      field: 'result',
      flex: 2,
      headerName: 'Result'
    }
  ];

  const silencedDataEntryTableColumns = [
    {
      field: 'location',
      headerName: 'Location',
      flex: 2,
      valueOptions: currentLocations,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: variation === 'spc' ? 'type' : 'method',
      headerName: variation === 'spc' ? 'Type' : 'Method',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentTypesDropdown : currentMethodsDropdown,
      flex: 2
    },
    {
      field: 'parameter',
      headerName: 'Parameter',
      flex: 2,
      valueOptions: (data) => {
        return renderDropdownParameter(data, data.field);
      },
      editable: true,
      type: 'singleSelect'
    },
    {
      field: variation === 'spc' ? 'method' : 'instrument',
      headerName: variation === 'spc' ? 'Method' : 'Instrument',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentMethodsDropdown : currentInstrumentsDropdown,
      flex: 2
    },
    {
      field: variation === 'spc' ? 'product' : 'sampleName',
      headerName: variation === 'spc' ? 'Product' : 'Sample Name',
      editable: true,
      type: 'singleSelect',
      valueOptions: variation === 'spc' ? currentProductsDropdown : currentSamplesDropdown,
      flex: 2
    },
    {
      field: 'dateTime',
      headerName: 'Date/Time',
      sortComparator: dayTimeComparator,
      editable: true,
      flex: 2,
      valueGetter: (params) => {
        return moment(params.value).format('MM/DD/YYYY HH:mm');
      }
    },
    {
      field: 'personnel',
      headerName: 'Personnel',
      flex: 2,
      valueOptions: currentPersonnelDropdown,
      editable: true,
      type: 'singleSelect'
    },
    {
      field: 'sampleId',
      headerName: 'Sample ID',
      editable: true,
      flex: 2
    },
    {
      field: 'setupData',
      headerName: 'Setup Data',
      renderCell: (params) => {
        if (params.value == 0 || params.value == 1) return params.value;
        return '';
      },
      editable: true,
      flex: 2
    },
    {
      field: 'silence',
      headerName: 'Silence?',
      editable: true,
      flex: 2
    },
    {
      field: 'silenceReason',
      headerName: 'Reason for Silencing',
      editable: true,
      flex: 2
    },
    {
      field: 'result',
      headerName: 'Result',
      editable: true,
      flex: 2
    }
  ];

  const silencedDataEntryTableColumnsReadOnly = [
    {
      field: 'location',
      headerName: 'Location',
      flex: 4
    },
    {
      field: variation === 'spc' ? 'type' : 'method',
      headerName: variation === 'spc' ? 'Type' : 'Method',
      flex: 2
    },
    {
      field: 'parameter',
      headerName: 'Parameter',
      flex: 2
    },
    {
      field: variation === 'spc' ? 'method' : 'instrument',
      headerName: variation === 'spc' ? 'Method' : 'Instrument',
      flex: 2
    },
    {
      field: variation === 'spc' ? 'product' : 'sampleName',
      headerName: variation === 'spc' ? 'Product' : 'Sample Name',
      flex: 2
    },
    {
      field: 'dateTime',
      headerName: 'Date/Time',
      sortComparator: dayTimeComparator,
      flex: 3,
      valueGetter: (params) => {
        return moment(params.value).format('MM/DD/YYYY HH:mm');
      }
    },
    {
      field: 'personnel',
      headerName: 'Personnel',
      flex: 3
    },
    {
      field: 'sampleId',
      headerName: 'Sample ID',
      flex: 3
    },
    {
      field: 'setupData',
      headerName: 'Setup Data',
      flex: 2
    },
    {
      field: 'silence',
      headerName: 'Silence?',
      flex: 2
    },
    {
      field: 'silenceReason',
      headerName: 'Reason for Silencing',
      flex: 2
    },
    {
      field: 'result',
      headerName: 'Result',
      flex: 2
    }
  ];

  const renderDataTableSection = (renderRadioValue) => {
    if (renderRadioValue === '1') {
      if (!recheckAlert) {
        return (
          <>
            <div className="overview-chart-header">
              <h4>Scheduled Data</h4>
            </div>
            {renderDataSuccessAlert(addDataAlert)}
            {addedConfigAlert(configAlert)}
            {renderScheduledDataTable(featureFlags)}
          </>
        );
      }
      return (
        <>
          <div className="overview-chart-header">
            <h4>Recheck Data</h4>
          </div>
          {renderAlert(recheckAlert)}
          <CustomTable
            numberOfRows={pageSizeDefault}
            data={dataToRecheck}
            header={recheckDataEntryTableColumns}
            action={['delete', 'edit']}
            deleteRowData={deleteRow}
          />
          <div className="overview-chart-header">
            <h4>Scheduled Data</h4>
          </div>
          <CustomTable
            numberOfRows={pageSizeDefault}
            data={currentScheduledSamples}
            setData={setCurrentScheduledSamples}
            header={dataEntryTableColumns}
            action={['delete', 'edit']}
            deleteRowData={deleteRow}
          />
        </>
      );
    }
    return (
      <>
        <div className="overview-chart-header">
          <h4>Silenced Data</h4>
        </div>
        <CustomTable
          numberOfRows={pageSizeDefault}
          data={currentSilencedData}
          header={silencedDataEntryTableColumns}
          action={['delete', 'edit']}
          deleteRowData={deleteRow}
        />
      </>
    );
  };

  const renderDataTableReadOnly = (renderRadioValue) => {
    if (renderRadioValue === '1') {
      if (!recheckAlert) {
        return (
          <>
            <div className="overview-chart-header">
              <h4>Scheduled Data</h4>
            </div>
            <CustomTable
              data={currentScheduledSamples}
              header={dataEntryTableColumnsReadOnly}
              numberOfRows={pageSizeDefault}
            />
          </>
        );
      }
      return (
        <>
          <div className="overview-chart-header">
            <h4>Recheck Data</h4>
          </div>
          {renderAlert(recheckAlert)}
          <CustomTable
            data={dataToRecheck}
            header={recheckDataEntryTableColumnsReadOnly}
            numberOfRows={pageSizeDefault}
          />
          <div className="overview-chart-header">
            <h4>Scheduled Data</h4>
          </div>
          <CustomTable
            data={currentScheduledSamples}
            header={dataEntryTableColumnsReadOnly}
            numberOfRows={pageSizeDefault}
          />
        </>
      );
    }
    return (
      <>
        <div className="overview-chart-header">
          <h4>Silenced Data</h4>
        </div>
        <CustomTable
          data={currentSilencedData}
          header={silencedDataEntryTableColumnsReadOnly}
          numberOfRows={pageSizeDefault}
        />
      </>
    );
  };

  const renderTablesWithPermissions = (renderRadioValue) => {
    const readOnly = checkReadOnly(permissionData, SCREEN?.DATA_ENTRY);
    if (readOnly) {
      return renderDataTableReadOnly(renderRadioValue);
    }
    return renderDataTableSection(renderRadioValue);
  };

  useEffect(() => {
    if (Array.isArray(methods) && methods.length > 0) {
      const methodsDropdown = [];
      methods.forEach((method) => {
        const methodToReturn = {
          id: method.id,
          value: `${method.method}`,
          label: `${method.method}`
        };
        const parameterToReturn = {
          value: method.parameter,
          label: method.parameter
        };
        if (!methodsDropdown.find((dropdownItem) => dropdownItem.value === `${method.method}`)) {
          methodsDropdown.push(methodToReturn);
        }
      });
      methodsDropdown.sort(textSort);
      setCurrentMethodsDropdown(methodsDropdown);
    }
  }, [methods]);

  // eslint-disable-next-line no-unused-vars
  const [columnToDisplay, setColumnToDisplay] = useState(dataEntryTableColumns);

  const deleteDataEntry = () => {
    if (deleteList.length > 0) {
      deleteList.forEach((item) => {
        deleteScheduledSample(item.id, variation);
      });
      setDeleteList([]);
    }
  };

  const saveParsedData = async (currentRowsToSave) => {
    setLoading(true);
    const addDataObject = {
      rows: currentRowsToSave,
      variation,
      isImportFromFile: true
    };

    const response = await addMultipleDataFunction(addDataObject);
    if (response.data && response.data.status === 200) {
      const runDataObject = {
        variation,
        listParameter: currentRowsToSave.map((r) => ({
          location: r.location,
          method: r.method,
          instrument: variation === 'spc' ? null : r.instrument,
          sampleName: variation === 'spc' ? null : r.sampleName,
          type: variation === 'spc' ? r.type : null,
          product: variation === 'spc' ? r.product : null
        }))
      };
      const response = await runMultipleDataFunction(runDataObject);
      if (response.data?.status === 200 && currentRowsToSave.length > 0) {
        const firstRow = currentRowsToSave[0];
        if (firstRow) {
          const runDataParams =
            variation === 'spc'
              ? {
                  location: firstRow.location,
                  type: firstRow.type,
                  method: firstRow.method,
                  product: firstRow.product,
                  parameter: firstRow.parameter
                }
              : {
                  location: firstRow.location,
                  method: firstRow.method,
                  instrument: firstRow.instrument,
                  sampleName: firstRow.sampleName,
                  parameter: firstRow.parameter
                };

          const runFunction = variation === 'spc' ? spcRunDataFunction : runDataFunction;
          await runFunction(runDataParams);
        }
        setAddDataAlert(true);
      }
    }

    setLoading(false);
  };

  const saveData = async (currentRowsToSave) => {
    const parsedRowsToSave = currentRowsToSave.filter((row) => row.result !== '');
    if (recheckAlert) {
      parsedRowsToSave.forEach((row) => {
        if (row.silence === 'Yes') {
          createSilencedData(row, variation);
        }
      });
    } else {
      setLoading(true);

      deleteDataEntry();

      const columnsToCheck =
        variation === 'spc'
          ? ['location', 'type', 'product', 'method', 'parameter']
          : ['location', 'method', 'sampleName', 'instrument', 'parameter'];

      const currentRowsToSave = parsedRowsToSave
        .map((rowIn) => ({
          ...rowIn,
          dateTime: moment(rowIn.dateTime, [
            'MM/DD/YYYY hh:mm A',
            'M/D/YYYY hh:mm A',
            'M/D/YYYY HH:mm',
            'MM/DD/YYYY HH:mm',
            'MM/DD/YYYY hh:mm', // Legacy format
            'M/D/YYYY hh:mm' // Legacy format
          ]).toISOString()
        }))
        .filter((row) => {
          for (const column of columnsToCheck) {
            if (row[column] === '') {
              return false;
            }
          }
          return true;
        });
      const rowsToAddNew = currentRowsToSave
        .filter((row) => !!row.isNew)
        .map((item) => {
          let fullKey;
          if (variation === 'spc') {
            fullKey = `${item.location}|${item.type}|${item.product}|${item.method}|${item.parameter}`;
          } else {
            fullKey = `${item.location}|${item.method}|${item.sampleName}|${item.instrument}|${item.parameter}`;
          }
          return {
            dateTime: dateTimeChanged.includes(item.id) ? item.dateTime : moment().toISOString(),
            fullKey,
            location: item.location,
            method: item.method,
            parameter: item.parameter,
            instrument: variation === 'spc' ? null : item.instrument,
            sampleName: variation === 'spc' ? null : item.sampleName,
            type: variation === 'spc' ? item.type : null,
            product: variation === 'spc' ? item.product : null
          };
        });

      if (currentRowsToSave.length === 0) {
        setLoading(false);
        return;
      }

      const addDataObject = {
        variation,
        rows: currentRowsToSave.map((item) => {
          delete item.isNew;
          delete item.delete;
          if (!item.lowerControlLimit) {
            delete item.lowerControlLimit;
          }
          if (!item.upperControlLimit) {
            delete item.upperControlLimit;
          }
          return {
            ...item,
            dateTime: item.dateTime || moment().toISOString()
          };
        })
      };
      const response = await addMultipleDataFunction(addDataObject);
      if (response.data && response.data.status === 200) {
        const runDataObject = {
          variation,
          listParameter: currentRowsToSave.map((r) => ({
            location: r.location,
            method: r.method,
            instrument: variation === 'spc' ? null : r.instrument,
            sampleName: variation === 'spc' ? null : r.sampleName,
            type: variation === 'spc' ? r.type : null,
            product: variation === 'spc' ? r.product : null
          }))
        };
        const response = await runMultipleDataFunction(runDataObject);
        if (response.data && response.data.status === 200) {
          rowsToAddNew.forEach((row) => {
            createScheduledSamples(row, variation);
          });
          currentRowsToSave.forEach((row) => {
            if (row.isNew) {
              return;
            }
            deleteScheduledSample(row.id, variation);
          });

          const firstRow = currentRowsToSave[0];
          if (firstRow) {
            const runDataParams =
              variation === 'spc'
                ? {
                    location: firstRow.location,
                    type: firstRow.type,
                    method: firstRow.method,
                    product: firstRow.product,
                    parameter: firstRow.parameter
                  }
                : {
                    location: firstRow.location,
                    method: firstRow.method,
                    instrument: firstRow.instrument,
                    sampleName: firstRow.sampleName,
                    parameter: firstRow.parameter
                  };

            const runFunction = variation === 'spc' ? spcRunDataFunction : runDataFunction;
            await runFunction(runDataParams);
          }

          setAddDataAlert(true);
        }
      }

      setLoading(false);
    }
  };

  const convertTimeInFile = (time) => {
    // parses date strings from file imports according to the client's timezone configuration
    // before converting to UTC for storage
    return moment.tz(
      time,
      ['MM/DD/YYYY hh:mm A', 'M/D/YYYY hh:mm A', 'MM/DD/YYYY HH:mm', 'M/D/YYYY HH:mm'],
      tz
    );
  };

  const handleResults = async (results) => {
    // Detect SLIMS format from the header row up-front, before mapping
    // data rows. Newer SLIMS exports add "Sale Tank Number" and "Sample
    // Information" between Product and Collection time, shifting later
    // columns by +2.
    const headerEntry = results[0] || [];
    let isSlims = false;
    let slimsSaleTankCol = -1;
    let slimsDateTimeCol = 4;
    let slimsMethodCol = 5;
    let slimsParamCol = 6;
    let slimsResultCol = 7;
    if (headerEntry.length >= 5) {
      isSlims =
        headerEntry[0] === 'Sample ID' &&
        headerEntry[1] === 'Type' &&
        headerEntry[2] === 'Product' &&
        (headerEntry[4] === 'Collection time' || headerEntry[3] === 'Sale Tank Number');
      if (isSlims && headerEntry[3] === 'Sale Tank Number') {
        slimsSaleTankCol = 3;
        slimsDateTimeCol = 5;
        slimsMethodCol = 6;
        slimsParamCol = 7;
        slimsResultCol = 8;
      }
    }
    // Starting 2026-04-01 ET, combine Product with Sale Tank Number when present.
    const slimsCutoff = moment.tz('2026-04-01', 'YYYY-MM-DD', 'America/New_York');

    const matchingUser = currentPersonnel.find((person) => person.email === auth.user.email);

    const currentRowsToUpdate = results
      .slice(1)
      .map((entry) => {
        if (entry[0]) {
          if (variation === 'spc') {
            // we do not support high-ascii characters, so replace them with underscores
            // across the full entry array
            entry = entry.map((e) =>
              String(e)
                .trim()
                .replace(/[^\x20-\x7E]/g, '_')
            );

            if (isSlims) {
              const location = config.defaultLocation;
              const type = entry[1];
              const rawProduct = entry[2];
              const saleTank =
                slimsSaleTankCol >= 0 ? String(entry[slimsSaleTankCol] || '').trim() : '';
              const dateTime = convertTimeInFile(entry[slimsDateTimeCol]);
              const product =
                saleTank && dateTime.isSameOrAfter(slimsCutoff)
                  ? `${rawProduct} ${saleTank}`
                  : rawProduct;
              const method = entry[slimsMethodCol];
              const parameter = entry[slimsParamCol] || entry[slimsMethodCol];
              return {
                location,
                type,
                product,
                method,
                parameter,
                dateTime: dateTime.utc().toISOString(),
                sampleId: entry[0],
                personnel: matchingUser
                  ? config.useAbbreviationPersonnelName
                    ? matchingUser?.abbreviation || matchingUser?.name || ''
                    : matchingUser?.name || ''
                  : auth.user.email,
                setupData: 1, // Blue Tide says setup data should always be 1
                result: entry[slimsResultCol],
                delete: ''
              };
            }

            return {
              location: entry[0],
              type: entry[1],
              parameter: entry[2],
              method: entry[3],
              product: entry[4],
              dateTime: convertTimeInFile(entry[5]).utc().toISOString(),
              personnel: entry[6],
              sampleId: entry[7],
              setupData: entry[8], // note this is inconsistent with SLIMS *and intended to be*
              result: entry[9],
              delete: ''
            };
          }

          return {
            location: entry[0],
            method: entry[1],
            parameter: entry[2],
            instrument: entry[3],
            sampleName: entry[4],
            dateTime: convertTimeInFile(entry[5]).utc().toISOString(),
            personnel: entry[6],
            sampleId: entry[7],
            setupData: entry[8],
            result: entry[9],
            mean: entry[10],
            sd3n: entry[11],
            sd3p: entry[12],
            delete: ''
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);
    await saveParsedData(currentRowsToUpdate);
  };

  const isValidValue = (val) => val !== undefined && val !== null && String(val).trim() !== '';

  const importControlStrategyConfig = (existingConfig, rowObj) => {
    const get = (key) => {
      const importedValue = rowObj[key];
      return isValidValue(importedValue) ? importedValue : existingConfig?.[key];
    };
    const controlStrategy = get('controlStrategy');
    const strategyDefaults = {
      EWMA: {
        controlLimitViolation: 'Out of Control',
        warningLimitViolation: 'Off',
        twoOfThree: 'Off',
        fiveConsecutive: 'Off',
        nineConsecutive: 'Out of Control',
        sevenConsecutive: 'Off',
        ewmaTrend: 'Out of Control',
        controlLimitExceedance: 'Off',
        toleranceLimitExceedance: 'Off',
        singlePointViolation: 'Warning',
        fivePointViolation: 'Out of Control',
        andersonDarling: 'Off',
        tpiOnePointTwo: 'Off'
      },
      'Run rules': {
        controlLimitViolation: 'Out of Control',
        warningLimitViolation: 'Off',
        twoOfThree: 'Out of Control',
        fiveConsecutive: 'Out of Control',
        nineConsecutive: 'Out of Control',
        sevenConsecutive: 'Out of Control',
        ewmaTrend: 'Off',
        controlLimitExceedance: 'Off',
        toleranceLimitExceedance: 'Off',
        singlePointViolation: 'Warning',
        fivePointViolation: 'Out of Control',
        andersonDarling: 'Off',
        tpiOnePointTwo: 'Off'
      },
      'Control limits': {
        controlLimitViolation: 'Off',
        warningLimitViolation: 'Off',
        twoOfThree: 'Off',
        fiveConsecutive: 'Off',
        nineConsecutive: 'Off',
        sevenConsecutive: 'Off',
        ewmaTrend: 'Off',
        controlLimitExceedance: 'Out of Control',
        toleranceLimitExceedance: 'Off',
        singlePointViolation: 'Off',
        fivePointViolation: 'Off',
        andersonDarling: 'Off',
        tpiOnePointTwo: 'Off'
      },
      default: {
        controlLimitViolation: 'Off',
        warningLimitViolation: 'Off',
        twoOfThree: 'Off',
        fiveConsecutive: 'Off',
        nineConsecutive: 'Off',
        sevenConsecutive: 'Off',
        ewmaTrend: 'Off',
        controlLimitExceedance: 'Off',
        toleranceLimitExceedance: 'Out of Control',
        singlePointViolation: 'Off',
        fivePointViolation: 'Off',
        andersonDarling: 'Off',
        tpiOnePointTwo: 'Off'
      }
    };
    const defaultValues = strategyDefaults[controlStrategy] || strategyDefaults.default;
    const ruleKeys = Object.keys(strategyDefaults.default);
    const result = {
      id: existingConfig?.id || '',
      location: get('location'),
      method: get('method'),
      parameter: get('parameter'),
      instrument: get('instrument'),
      sampleName: get('sampleName'),
      pretreatData: get('pretreatData'),
      controlStrategy,
      duiChart: get('duiChart'),
      chartSigDigits: parseInt(get('chartSigDigits')) || 0,
      lowerToleranceLimit: get('lowerToleranceLimit'),
      upperToleranceLimit: get('upperToleranceLimit'),
      lowerControlLimit: get('lowerControlLimit'),
      upperControlLimit: get('upperControlLimit'),
      mean: get('mean'),
      standardDeviation: get('standardDeviation'),
      reproducibility: get('reproducibility'),
      repeatability: get('repeatability'),
      delete: ''
    };
    ruleKeys.forEach((key) => {
      const importedValue = rowObj[key];
      result[key] = isValidValue(importedValue) ? importedValue : defaultValues[key];
    });
    return result;
  };

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: '',
    messages: [],
    skip: null,
    runSkip: null,
    runCancel: null
  });

  const handleAddConfigs = async (configs) => {
    setConfigLoad(true);
    if (!configs || configs.length < 2) return;

    const toCamelCase = (str) =>
      str
        .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
        .replace(/^[A-Z]/, (chr) => chr.toLowerCase());

    const normalizedHeaders = configs[0].map(toCamelCase);

    let stopUpload = false;
    const parsedRows = [];

    for (let entryIndex = 1; entryIndex < configs.length; entryIndex++) {
      const entry = configs[entryIndex];

      const isEmpty =
        !entry ||
        entry.every((cell) => cell === undefined || cell === null || String(cell).trim() === '');
      if (isEmpty) continue;

      const rowObj = {};
      normalizedHeaders.forEach((key, i) => {
        const value = entry[i];
        rowObj[key] = value !== undefined && value !== '' ? value : null;
      });

      const { location, method, sampleName, instrument, parameter } = rowObj;
      if (!location || !method || !sampleName || !instrument || !parameter) continue;

      const fullKey = `${location}|${method}|${sampleName}|${instrument}|${parameter}`;

      const fetchControlStrategyConfig = await getControlStrategyConfigurationByFullKey(
        fullKey,
        variation
      );
      const isInvalid =
        fetchControlStrategyConfig == null ||
        (Array.isArray(fetchControlStrategyConfig) && fetchControlStrategyConfig.length === 0) ||
        (typeof fetchControlStrategyConfig === 'object' &&
          !Array.isArray(fetchControlStrategyConfig) &&
          Object.keys(fetchControlStrategyConfig).length === 0);

      if (isInvalid) {
        const styledMessage = (
          <div>
            <div>
              <strong>
                Found error in row no.{entryIndex + 1}: No configuration found for fullKey
              </strong>
            </div>
            <div className="mt-2">
              <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{fullKey}</code>
            </div>
          </div>
        );

        // Show modal and wait for user action
        await new Promise((resolve) => {
          setErrorModal({
            show: true,
            title: 'Configuration Error',
            messages: [styledMessage],
            textButton: 'Cancel Upload',
            skip: 'Skip Row',
            runSkip: () => {
              setErrorModal((prev) => ({ ...prev, show: false }));
              resolve('skip');
            },
            runCancel: () => {
              stopUpload = true;
              setErrorModal((prev) => ({ ...prev, show: false }));
              resolve('cancel');
            }
          });
        });

        if (stopUpload) break;
        else continue; // skip row
      }

      const existingConfig = Array.isArray(fetchControlStrategyConfig)
        ? fetchControlStrategyConfig[0]
        : fetchControlStrategyConfig;
      const config = importControlStrategyConfig(existingConfig, rowObj);
      parsedRows.push(config);
    }

    if (stopUpload) {
      setConfigLoad(false);
      return;
    }

    // Process valid rows
    for (const config of parsedRows) {
      if (config.id && String(config.id).trim() !== '') {
        await updateConfiguration(config.id, config);
      } else {
        await createConfiguration(config);
      }
      await runDataFunction({
        location: config.location,
        method: config.method,
        instrument: config.instrument,
        sampleName: config.sampleName,
        parameter: config.parameter
      });
    }

    setConfigLoad(false);
    setConfigAlert(true);
  };

  const handleImportRows = async (results) => {
    if (!results || results.length < 2) return;

    const headerAliasMap = {
      'sample id (result id)': 'sampleId',
      'sample id': 'sampleId',
      'sample name': 'sampleName',
      'setup data': 'setupData',
      'date/time': 'dateTime',
      date: 'dateTime'
    };

    const normalizeHeader = (header) => {
      const raw = header?.trim().toLowerCase();
      const compact = raw.replace(/[^a-z0-9]/gi, '');
      return headerAliasMap[raw] || headerAliasMap[compact] || compact;
    };

    const headers = results[0].map(normalizeHeader);
    const dataRows = results.slice(1);

    const importedRows = dataRows
      .map((entry, rowIndex) => {
        if (!entry || !entry.length || !entry.some((e) => e && e.trim())) return null;

        const rowObject = {};
        headers.forEach((key, i) => {
          rowObject[key] = entry[i]?.trim?.() || '';
        });

        const dateTime = convertTimeInFile(rowObject.dateTime).format('MM/DD/YYYY h:mm A') || '';

        // Pull all possible fields
        const location = rowObject.location || '';
        const method = rowObject.method || '';
        const parameter = rowObject.parameter || '';
        const product = rowObject.product || '';
        const type = rowObject.type || '';
        const sampleName = rowObject.sampleName || '';
        const instrument = rowObject.instrument || '';

        // Compute fullKey depending on variation
        let fullKey = '';
        if (variation === 'spc') {
          fullKey = [location, type, product, method, parameter].join('|');
        } else {
          fullKey = [location, method, sampleName, instrument, parameter].join('|');
        }

        return {
          location,
          method,
          parameter,
          product,
          type,
          sampleName: sampleName || null,
          instrument: instrument || null,
          sampleId: rowObject.sampleId || '',
          dateTime: dateTime,
          recType: 'scheduled_samples',
          fullKey
        };
      })
      .filter(Boolean);

    if (!importedRows.length) {
      alert('No valid rows were imported. Please check the file format and try again.');
      return;
    }

    setCurrentScheduledSamples((prev) => [...prev, ...importedRows]);

    try {
      await Promise.all(importedRows.map((row) => createScheduledSamples(row, variation)));
    } catch (error) {
      console.error('Error saving scheduled samples:', error);
      alert('Some rows failed to save. Check console for details.');
    }
  };

  return (
    <div>
      <div className="header-button-container">
        <ButtonGroup>
          {radios.map((radio, idx) => (
            <ToggleButton
              key={radio.value}
              id={`radio-${idx}`}
              type="radio"
              variant={idx % 2 ? 'outline-primary' : 'outline-primary'}
              name="radio"
              value={radio.value}
              checked={radioValue === radio.value}
              onChange={handleRadioChange}
            >
              {radio.name}
            </ToggleButton>
          ))}
        </ButtonGroup>
      </div>
      <div className="data-entry-container">
        <Col>{renderTablesWithPermissions(radioValue)}</Col>
      </div>
      <div className="data-button-container">
        <div className="left-buttons">
          <div>
            {renderButtonWithPermissions(
              'Add Row',
              () => addRow(),
              SCREEN?.DATA_ENTRY,
              RULES.CREATE,
              permissionData
            )}
          </div>
          {renderAuthButtonWithPermission(
            handleResults,
            auth,
            currentPersonnel,
            permissionData,
            SCREEN?.DATA_ENTRY,
            RULES.CREATE,
            isLoading,
            'Add Data From File'
          )}
          {renderAuthButtonWithPermission(
            handleImportRows,
            auth,
            currentPersonnel,
            permissionData,
            SCREEN?.DATA_ENTRY,
            RULES.CREATE,
            importLoading,
            'Add List To Schedule'
          )}

          {renderAuthButtonWithPermission(
            handleAddConfigs,
            auth,
            currentPersonnel,
            permissionData,
            SCREEN?.CONFIG,
            RULES.CREATE,
            configLoad,
            'Add configurations from file'
          )}
          {!isSpcPage &&
            renderButtonWithPermissions(
              'Download Complete Dataset List',
              () => exportFullKeysToCSV(currentLocation),
              SCREEN?.DATA,
              RULES.SAVE,
              permissionData,
              true,
              isLoading
            )}
        </div>
        <div>
          {renderButtonWithPermissions(
            'Save Data',
            () => saveData(currentScheduledSamples),
            SCREEN?.DATA_ENTRY,
            RULES.SAVE,
            permissionData,
            true,
            isLoading
          )}
        </div>
      </div>
      <ModalError
        show={errorModal.show}
        setShow={(show) => setErrorModal((prev) => ({ ...prev, show }))}
        title={errorModal.title}
        messages={errorModal.messages}
        textButton={errorModal.textButton}
        runSkip={errorModal.runSkip}
        runCancel={errorModal.runCancel}
        skip={errorModal.skip}
      />
    </div>
  );
}

export default DataEntrySection;
